import type { NdtAssetAllocation, NdtAssetAllocationRepository } from "../../domain/ndt/ndt-asset-allocation.js";
import type { NdtAssetReportUsage } from "./ndt-asset-traceability.js";
import { paginateNdtAssets, type NdtAssetListPage, type NdtAssetListQuery } from "../../domain/ndt/ndt-asset-list-query.js";
import type { RequestContext } from "../context/request-context.js";
import { AppError, ErrorCode } from "../../shared/errors/app-error.js";
import type { NdtAssetRepository } from "../../domain/ndt/ndt-asset-repository.js";
import type { AuditService } from "./audit-service.js";
import type { NdtAsset, NdtAssetId, NdtAssetType } from "../../domain/ndt/ndt-assets.js";
import type { NdtAssetCertificate, NdtAssetVerification, NdtAssetSnapshot } from "../../domain/ndt/ndt-asset-history.js";
import { resolveCalibrationStatus } from "../../domain/ndt/ndt-calibration.js";
import type { ClientId } from "../../shared/kernel/identifiers.js";

import { RbacAuthorizationService } from "../../permissions/authorization.js";
import { PermissionCode } from "../../permissions/permissions.js";
import { resolveEffectivePermissions } from "../../permissions/effective-permissions.js";

const randomUUID = () => globalThis.crypto.randomUUID();

/** Answers whether an END asset is referenced by any report, so a used asset is never physically deleted. */
export type NdtAssetUsageChecker = (assetId: NdtAssetId) => Promise<boolean>;

export class NdtAssetService {
  constructor(
    public repository: NdtAssetRepository,
    private auditRecorder: any,
    private isAssetInUse?: NdtAssetUsageChecker,
    private listAssetUse?: (context: RequestContext, assetId: NdtAssetId) => Promise<NdtAssetReportUsage[]>,
    private allocations?: NdtAssetAllocationRepository,
    private projectBelongsToClient?: (projectId: string, clientId: string) => Promise<boolean>
  ) {}

  private checkPermission(context: RequestContext, permission: PermissionCode) {
    if (context && (!context.permissions || typeof (context.permissions as any).has !== "function")) {
      const role = (context as any).userRole || (context as any).role;
      if (role) {
        const resolved = resolveEffectivePermissions(role);
        new RbacAuthorizationService().require({ ...context, permissions: resolved }, permission);
        return;
      }
    }
    new RbacAuthorizationService().require(context, permission);
  }

  // --- CRUD Assets ---

  async createAsset(context: RequestContext, assetData: Partial<NdtAsset>): Promise<NdtAsset> {
    this.checkPermission(context, PermissionCode.NdtRegistryCreate);

    if (!assetData.assetType || !assetData.serialNumber) {
      throw new AppError(ErrorCode.Validation, "assetType and serialNumber are required.");
    }

    const newAsset: NdtAsset = {
      ...assetData,
      id: `NDT-${randomUUID()}`,
      status: 'ACTIVE',
      createdAt: new Date().toISOString(),
      createdBy: context.userId || 'system',
      updatedAt: new Date().toISOString(),
      updatedBy: context.userId || 'system'
    } as NdtAsset;

    await this.repository.saveAsset(newAsset);
    if (this.auditRecorder && this.auditRecorder.record) {
      await this.auditRecorder.record(context, 'NDT_ASSET_CREATED', newAsset.id);
    }
    
    return newAsset;
  }

  async updateAsset(context: RequestContext, assetId: NdtAssetId, updates: Partial<NdtAsset>): Promise<NdtAsset> {
    this.checkPermission(context, PermissionCode.NdtRegistryUpdate);
    
    const asset = await this.repository.getAsset(assetId);
    if (!asset) throw new AppError(ErrorCode.NotFound, "NDT Asset not found.");

    const updatedAsset = {
      ...asset,
      ...updates,
      updatedAt: new Date().toISOString(),
      updatedBy: context.userId || 'system'
    } as NdtAsset;

    await this.repository.saveAsset(updatedAsset);
    if (this.auditRecorder && this.auditRecorder.record) {
      await this.auditRecorder.record(context, 'NDT_ASSET_UPDATED', assetId);
    }

    return updatedAsset;
  }

  async listAssets(context: RequestContext, type?: NdtAssetType, includeInactive: boolean = false): Promise<NdtAsset[]> {
    this.checkPermission(context, PermissionCode.NdtRegistryRead);
    return this.repository.listAssets(type, includeInactive);
  }

  /** M21 C2 — busca backend paginada, com RBAC e fallback só para memória. */
  async listAssetsPaged(context: RequestContext, query: NdtAssetListQuery): Promise<NdtAssetListPage> {
    this.checkPermission(context, PermissionCode.NdtRegistryRead);
    if (this.repository.listAssetsPaged) return this.repository.listAssetsPaged(query);
    return paginateNdtAssets(await this.repository.listAssets(undefined, true), query);
  }

  async getAsset(context: RequestContext, assetId: NdtAssetId): Promise<NdtAsset> {
    this.checkPermission(context, PermissionCode.NdtRegistryRead);
    const asset = await this.repository.getAsset(assetId);
    if (!asset) throw new AppError(ErrorCode.NotFound, "NDT Asset not found.");
    return asset;
  }

  async inactivateAsset(context: RequestContext, assetId: NdtAssetId, reason: string): Promise<void> {
    this.checkPermission(context, PermissionCode.NdtRegistryInactivate);

    const asset = await this.repository.getAsset(assetId);
    if (!asset) throw new AppError(ErrorCode.NotFound, "NDT Asset not found.");

    asset.status = 'INACTIVE';
    asset.inactivatedAt = new Date().toISOString();
    asset.inactivatedBy = context.userId || 'system';
    asset.inactivationReason = reason;

    await this.repository.saveAsset(asset);
    if (this.auditRecorder && this.auditRecorder.record) {
      await this.auditRecorder.record(context, 'NDT_ASSET_INACTIVATED', assetId);
    }
  }

  async reactivateAsset(context: RequestContext, assetId: NdtAssetId): Promise<void> {
    this.checkPermission(context, PermissionCode.NdtRegistryUpdate);
    
    const asset = await this.repository.getAsset(assetId);
    if (!asset) throw new AppError(ErrorCode.NotFound, "NDT Asset not found.");

    asset.status = 'ACTIVE';
    delete asset.inactivatedAt;
    delete asset.inactivatedBy;
    delete asset.inactivationReason;
    asset.updatedAt = new Date().toISOString();
    asset.updatedBy = context.userId || 'system';

    await this.repository.saveAsset(asset);
    if (this.auditRecorder && this.auditRecorder.record) {
      await this.auditRecorder.record(context, 'NDT_ASSET_REACTIVATED', assetId);
    }
  }

  async deleteAsset(context: RequestContext, assetId: NdtAssetId, reason: string): Promise<void> {
    this.checkPermission(context, PermissionCode.NdtRegistryDelete);

    // An asset already used by a report is documentary evidence of how that
    // measurement was taken — it may only be inactivated, never deleted.
    if (this.isAssetInUse && await this.isAssetInUse(assetId)) {
      throw new AppError(ErrorCode.Conflict, "NDT asset is referenced by a report", {
        safeMessage: "Este equipamento já foi utilizado em um relatório e não pode ser excluído. Utilize a opção de Inativar."
      });
    }

    // Check if it has history
    const certs = await this.repository.listCertificates(assetId);
    const verifs = await this.repository.listVerifications(assetId);

    if (certs.length > 0 || verifs.length > 0) {
      throw new AppError(ErrorCode.Validation, "HARD DELETE DEVE SER NEGADO: Este equipamento possui histórico.", {
        safeMessage: "Este equipamento possui histórico de calibração/verificação e não pode ser excluído. Utilize a opção de Inativar."
      });
    }

    await this.repository.deleteAsset(assetId);
    if (this.auditRecorder && this.auditRecorder.record) {
      await this.auditRecorder.record(context, 'NDT_ASSET_DELETED', assetId);
    }
  }

  // --- Certificates ---

  async addCertificate(context: RequestContext, assetId: NdtAssetId, certData: Partial<NdtAssetCertificate>): Promise<NdtAssetCertificate> {
    this.checkPermission(context, PermissionCode.NdtRegistryUpdate);

    const asset = await this.repository.getAsset(assetId);
    if (!asset) throw new AppError(ErrorCode.NotFound, "NDT Asset not found.");

    // Status is always derived from the validity date — never typed in.
    const status = resolveCalibrationStatus(certData.expirationDate);

    const cert: NdtAssetCertificate = {
      ...certData,
      id: `CERT-${randomUUID()}`,
      assetId,
      createdAt: new Date().toISOString(),
      status
    } as NdtAssetCertificate;

    await this.repository.saveCertificate(cert);
    
    // Certificado não é ordem de liberação de manutenção/inatividade.
    // Apenas os estados de calibração podem mudar automaticamente.
    if (asset.status === 'ACTIVE' || asset.status === 'CALIBRATION_DUE' || asset.status === 'CALIBRATION_EXPIRED') {
      if (status === 'VALID') asset.status = 'ACTIVE';
      if (status === 'EXPIRED') asset.status = 'CALIBRATION_EXPIRED';
    }
    await this.repository.saveAsset(asset);

    if (this.auditRecorder && this.auditRecorder.record) {
      await this.auditRecorder.record(context, 'NDT_CERTIFICATE_ADDED', cert.id);
    }
    
    return cert;
  }

  /** Registros reais de uso documental, limitados ao Cliente autenticado. */
  async listAllocations(context: RequestContext, assetId: NdtAssetId): Promise<NdtAssetAllocation[]> {
    this.checkPermission(context, PermissionCode.NdtRegistryRead);
    await this.getAsset(context, assetId);
    if (!context.activeClientId || !this.allocations) return [];
    return (await this.allocations.listByAsset(assetId))
      .filter(a => a.clientId === String(context.activeClientId));
  }

  async allocateAsset(context: RequestContext, assetId: NdtAssetId,
    input: { projectId?: string | null; professionalId?: string | null; notes?: string } = {}): Promise<NdtAssetAllocation> {
    this.checkPermission(context, PermissionCode.NdtRegistryUpdate);
    if (!context.activeClientId || !context.userId || !this.allocations) {
      throw new AppError(ErrorCode.Authorization, "Contexto autorizado de Cliente obrigatório.");
    }
    const clientId = String(context.activeClientId);
    const asset = await this.getAsset(context, assetId);
    if (asset.status !== "ACTIVE") {
      throw new AppError(ErrorCode.Conflict, "Instrumento indisponível para alocação.");
    }
    const usage = await this.validateAssetUsage(context, assetId);
    if (!usage.allowed) throw new AppError(ErrorCode.Conflict, "Instrumento sem calibração/liberação válida.");
    const projectId = input.projectId || null;
    if (projectId && (!this.projectBelongsToClient || !await this.projectBelongsToClient(projectId, clientId))) {
      throw new AppError(ErrorCode.Authorization, "Projeto incompatível com o Cliente da alocação.");
    }
    if (await this.allocations.activeForAsset(assetId)) {
      throw new AppError(ErrorCode.Conflict, "Instrumento já está alocado.");
    }
    const allocation: NdtAssetAllocation = {
      id: `NDTALLOC-${randomUUID()}`, assetId, clientId, projectId,
      professionalId: input.professionalId || null,
      assignedByUserId: String(context.userId), assignedAt: new Date().toISOString(),
      returnedAt: null, returnedByUserId: null, notes: String(input.notes || "").slice(0, 500)
    };
    await this.allocations.create(allocation);
    if (this.auditRecorder?.record) await this.auditRecorder.record(context, "NDT_ASSET_ALLOCATED", allocation.id);
    return allocation;
  }

  async returnAsset(context: RequestContext, assetId: NdtAssetId, allocationId: string): Promise<void> {
    this.checkPermission(context, PermissionCode.NdtRegistryUpdate);
    if (!context.activeClientId || !this.allocations || !context.userId) {
      throw new AppError(ErrorCode.Authorization, "Cliente não autorizado.");
    }
    const clientId = String(context.activeClientId);
    const records = await this.allocations.listByAsset(assetId);
    const match = records.find(a => a.id === allocationId && a.clientId === clientId && !a.returnedAt);
    if (!match || !await this.allocations.close(match.id,clientId,String(context.userId),new Date().toISOString())) {
      throw new AppError(ErrorCode.Conflict, "Alocação não encontrada ou já devolvida.");
    }
    if (this.auditRecorder?.record) await this.auditRecorder.record(context, "NDT_ASSET_RETURNED", allocationId);
  }

  async listAssetUsage(context: RequestContext, assetId: NdtAssetId): Promise<NdtAssetReportUsage[]> {
    this.checkPermission(context, PermissionCode.NdtRegistryRead);
    await this.getAsset(context, assetId);
    return this.listAssetUse ? this.listAssetUse(context, assetId) : [];
  }

  async listCertificates(context: RequestContext, assetId: NdtAssetId): Promise<NdtAssetCertificate[]> {
    this.checkPermission(context, PermissionCode.NdtRegistryRead);
    return this.repository.listCertificates(assetId);
  }

  // --- Verifications ---

  async addVerification(context: RequestContext, assetId: NdtAssetId, verData: Partial<NdtAssetVerification>): Promise<NdtAssetVerification> {
    this.checkPermission(context, PermissionCode.NdtRegistryUpdate);
    const ver: NdtAssetVerification = {
      ...verData,
      id: `VER-${randomUUID()}`,
      assetId,
      createdAt: new Date().toISOString()
    } as NdtAssetVerification;

    await this.repository.saveVerification(ver);
    if (this.auditRecorder && this.auditRecorder.record) {
      await this.auditRecorder.record(context, 'NDT_VERIFICATION_ADDED', ver.id);
    }
    return ver;
  }

  async listVerifications(context: RequestContext, assetId: NdtAssetId): Promise<NdtAssetVerification[]> {
    this.checkPermission(context, PermissionCode.NdtRegistryRead);
    return this.repository.listVerifications(assetId);
  }

  // --- Snapshots ---
  
  async createSnapshot(context: RequestContext, assetId: NdtAssetId): Promise<NdtAssetSnapshot> {
    const asset = await this.getAsset(context, assetId);
    const certs = await this.listCertificates(context, assetId);
    const latestCert = certs.length > 0 ? certs[0] : null; // Certificates are sorted DESC by created date

    const snapshot: NdtAssetSnapshot = {
      id: `NDTSNAP-${randomUUID()}`,
      clientId: context.activeClientId as ClientId,
      assetId: asset.id,
      assetType: asset.assetType,
      internalCode: asset.internalCode,
      manufacturer: asset.manufacturer,
      model: asset.model,
      serialNumber: asset.serialNumber,
      technicalDataJson: JSON.stringify((asset as any).technicalData || {}),
      certificateNumber: latestCert ? latestCert.certificateNumber : "",
      certificateDate: latestCert ? latestCert.issueDate : "",
      expirationDate: latestCert && latestCert.expirationDate ? latestCert.expirationDate : "",
      timestamp: new Date().toISOString()
    };

    if (asset.assetType === 'CALIBRATION_BLOCK') {
      const blockAsset = asset as any;
      if (blockAsset.steps) {
        snapshot.stepsSnapshot = JSON.parse(JSON.stringify(blockAsset.steps));
      }
    }

    await this.repository.saveSnapshot(snapshot);
    return snapshot;
  }

  // --- Checks / Validations ---

  /**
   * Avaliação no dia efetivo da medição, sem depender de status armazenado no certificado.
   * Um bloqueio não pode ser convertido em aprovação por falta de política configurada.
   * Registros documentais anteriores não são recalculados por esta rotina.
   */
  async validateAssetUsage(
    context: RequestContext,
    assetId: NdtAssetId,
    measurementDate?: string
  ): Promise<{ allowed: boolean, warning?: string, requiresOverride: boolean }> {
    const asset = await this.getAsset(context, assetId);
    const reference = measurementDate ? new Date(measurementDate) : new Date();
    if (measurementDate && Number.isNaN(reference.getTime())) {
      throw new AppError(ErrorCode.Validation, "Data de medição inválida.");
    }
    const certs = await this.listCertificates(context, assetId);
    // Não usar certificado emitido após a medição para validar um ensaio histórico.
    // A comparação é por data civil (YYYY-MM-DD), evitando expiração no início do dia.
    const when = reference.toISOString().slice(0, 10);
    const eligible = certs.filter(cert => {
      const issued = String(cert.issueDate || "").slice(0, 10);
      return issued && issued <= when;
    }).sort((a, b) => String(b.issueDate).localeCompare(String(a.issueDate)) ||
      String(b.createdAt).localeCompare(String(a.createdAt)));
    const latestCert = eligible[0];

    let condition = 'OK';
    if (asset.status === 'INACTIVE') condition = 'INACTIVE';
    else if (asset.status === 'OUT_OF_SERVICE' || asset.status === 'UNDER_MAINTENANCE') condition = 'OUT_OF_SERVICE';
    else if (['ULTRASONIC_THICKNESS_GAUGE', 'MAGNETIC_YOKE', 'CALIBRATION_BLOCK', 'PYROMETER'].includes(asset.assetType)) {
      const calibration = latestCert ? resolveCalibrationStatus(
        latestCert.expirationDate,
        new Date(`${when}T00:00:00.000Z`)
      ) : 'NO_EXPIRATION_DEFINED';
      if (!latestCert) condition = 'MISSING_CERTIFICATE';
      else if (calibration === 'EXPIRED') condition = 'EXPIRED_CERTIFICATE';
      else if (calibration === 'NO_EXPIRATION_DEFINED') condition = 'UNKNOWN_CERTIFICATE_VALIDITY';
    }
    // Falhas técnicas críticas nunca são reduzidas a ALLOW/WARN por configuração isolada.
    // Exceção de engenharia exige fluxo próprio de autorização/auditoria, ainda não concedido aqui.
    if (condition === 'INACTIVE' || condition === 'OUT_OF_SERVICE') {
      return { allowed: false, requiresOverride: false };
    }
    if (condition === 'EXPIRED_CERTIFICATE' || condition === 'MISSING_CERTIFICATE' ||
        condition === 'UNKNOWN_CERTIFICATE_VALIDITY') {
      return { allowed: false, requiresOverride: true };
    }
    const policy = await this.repository.getPolicy(asset.assetType, condition);
    if (policy?.action === 'BLOCK') return { allowed: false, requiresOverride: policy.requiresEngineerOverride };
    if (policy?.action === 'WARN') return { allowed: true, warning: policy.severity, requiresOverride: false };
    return { allowed: true, requiresOverride: false };
  }

  async saveOverrideLog(context: RequestContext, log: any): Promise<void> {
    this.checkPermission(context, PermissionCode.NdtRegistryUpdate);
    await this.repository.saveOverrideLog(log);
  }
}
